<template>
  <div>
    <div
      ref="imgsRef"
      style="display:none;"
    >
      <slot
        :images="images"
        :options="options"
      >
        <img
          v-for="(src, index) in images"
          :key="`${src}-${index}`"
          :src="src"
        >
      </slot>
    </div>
    <div ref="modalRef" />
    <BeerButton
      v-if="images.length > 0 && currentIndex > 0"
      :handle-click="goPrev"
      :icon-bs="{
        mr: 0,
        size: 19,
      }"
      :bs="{
        ...$options.PAGER_BS,
        transform: 'rotate(180deg)',
        left: 87,
      }"
      variant="default"
      modifier=""
      icon="arrow-right-center-m"
      size="nm"
    />
    <BeerButton
      v-if="images.length > 0 && currentIndex < images.length - 1"
      :handle-click="goNext"
      :icon-bs="{
        mr: 0,
        size: 19,
      }"
      :bs="{
        ...$options.PAGER_BS,
        right: 87,
      }"
      variant="default"
      modifier=""
      icon="arrow-right-center-m"
      size="nm"
    />
  </div>
</template>

<script lang="ts">
  import {
    defineComponent, PropType, computed, ref,
    onBeforeUnmount, watch,
  } from 'vue'
  import 'viewerjs/dist/viewer.css'
  import Viewer from 'viewerjs'
  import { BeerCls } from '@xhs/yam-beer/core/ds'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'

  const PAGER_BS = {
    height: 48,
    zIndex: 2019,
    top: '42.66%',
    position: 'fixed',
    bg: 'transparent',
    mr: 0,
    px: 10,
    '&:hover': {
      bg: 'rgba(0,0,0,0.1)',
    },
    ':active': {
      bg: 'rgba(0,0,0,0.2)',
    },
  }

  const viewerCls = BeerCls({
    '&.viewer-backdrop': {
      bg: 'rgba(0,0,0,0.6)',
    },
    '.viewer-canvas': {
      top: '48px',
      bottom: '146px',
      overflow: 'visible',
    },
    '.viewer-navbar': {
      bg: 'rgba(0,0,0,0.4)',
    },
    '.viewer-toolbar': {
      '& > ul': {
        mb: '36px',
        py: '8px',
        px: '16px',
        bg: 'rgba(0,0,0,0.4)',
        borderRadius: '4px',
        '& > li': {
          borderRadius: '4px',
          bg: 'unset',
          '&:hover': {
            bg: 'rgba(255,255,255,0.1)',
          },
        },
        '& > li + li': {
          ml: '16px',
        },
      },
    },
    '.viewer-title': {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      maxWidth: '100%',
      display: 'flex',
      alignItems: 'center',
      bg: 'rgba(0,0,0,0.4)',
      height: '48px',
      m: 0,
      pl: '32px',
      fontSize: '14px',
      color: 'white',
    },
  })

  const DEFAULT_CONF = {
    zoomIn: 1,
    zoomOut: 1,
    oneToOne: 1,
    reset: 1,
    rotateLeft: 1,
    rotateRight: 1,
    flipHorizontal: 1,
    flipVertical: 1,
  }

  const MINIMAL_CONF = {
    zoomIn: 1,
    zoomOut: 1,
    reset: 1,
    rotateLeft: 1,
    rotateRight: 1,
  }
  export default defineComponent({
    name: 'ViewerComponent',
    components: {
      BeerButton,
    },
    props: {
      /**
       * 图片列表，string 类型
       */
      images: {
        type: Array as PropType<string[]>,
      },
      show: {
        type: Boolean,
        default: false,
      },
      /**
       * 透传给 viewerjs 的属性
       */
      options: {
        type: Object,
        default: () => ({}),
      },
      /**
       * toolbar 的类型，目前只有 minimal 和 default
       */
      toolbarVariant: {
        type: String as PropType<'minimal'|'default'>,
        default: 'minimal',
      },
    },
    emits: ['hidden'],
    setup(props, { emit }) {
      const currentIndex = ref(props.options.initialViewIndex || 0)
      const imgsRef = ref<HTMLElement|null>(null)
      const modalRef = ref<HTMLElement|null>(null)
      const toolbar = computed(() => {
        if (props.toolbarVariant === 'minimal') {
          return MINIMAL_CONF
        }
        return DEFAULT_CONF
      })
      let viewer: InstanceType<typeof Viewer>|null
      const clearViewer = () => {
        if (viewer) {
          viewer.destroy()
          viewer = null
        }
        if (currentIndex.value !== 0) {
          currentIndex.value = 0
        }
      }
      const createViewer = (images: string[]) => {
        if (!imgsRef.value || !modalRef.value) {
          return
        }
        viewer = new Viewer(
          imgsRef.value,
          {
            hidden: () => {
              clearViewer()
              emit('hidden')
            },
            view: ({ detail }) => {
              currentIndex.value = detail.index
            },
            container: modalRef.value,
            minZoomRatio: 0.01,
            maxZoomRatio: 100,
            movable: false,
            scalable: false,
            toggleOnDblclick: false,
            button: true,
            fullscreen: false,
            className: viewerCls,
            navbar: (images || []).length > 1,
            toolbar: toolbar.value,
            ...props.options,
          },
        )
        viewer.view(currentIndex.value)
      }
      watch(() => props.show, val => {
        if (!val) {
          clearViewer()
          return
        }
        currentIndex.value = props.options.initialViewIndex
        createViewer(props.images || [])
      }, {
        immediate: true,
      })
      onBeforeUnmount(() => {
        clearViewer()
      })
      return {
        goNext() {
          if (viewer) {
            viewer.next()
          }
        },
        goPrev() {
          if (viewer) {
            viewer.prev()
          }
        },
        modalRef,
        imgsRef,
        PAGER_BS,
        toolbar,
        currentIndex,
      }
    },
  })
</script>
